/**
 * ArchetypeImpactDisclosure — RC2.
 *
 * Inline "How this affects the report" disclosure surfaced beneath the
 * reader-profile dropdown (site landing + wizard). Reads the active
 * archetype meta out of COMPANY_ARCHETYPES and presents what the
 * report-generation prompt will do differently because of it. Renders
 * as a native <details> element so the disclosure widget is keyboard-
 * and screen-reader accessible without state machinery.
 *
 * Single source of truth for the labels (readerLens, risk tolerance,
 * typical decisions, narrative shifts) is the static archetype seed,
 * so this component never duplicates copy that lives in
 * src/types/company.ts.
 *
 * Renders a neutral-framing fallback when `archetype` is null so the
 * user is never staring at a blank disclosure on a site that has no
 * archetype set anywhere up the chain.
 */
import React from 'react';
import { COMPANY_ARCHETYPES, type CompanyArchetype } from '../../types/company';

interface Props {
  /** The currently effective archetype — already resolved through the
   *  per-report → site → company chain by the caller. Null means
   *  "no archetype set anywhere" and renders the neutral-framing
   *  copy instead of a disclosure. */
  archetype: CompanyArchetype | null;
  /** If true, render the disclosure expanded by default. Site landing
   *  defaults to collapsed (saves vertical space); tests pass true to
   *  assert the inner copy. */
  defaultOpen?: boolean;
  /** Optional test-id prefix; useful when the same disclosure is mounted
   *  at multiple surfaces (site landing + wizard) and tests need to
   *  distinguish them. Defaults to `archetype-impact`. */
  testIdPrefix?: string;
}

const RISK_LABEL: Record<'low' | 'medium' | 'high', string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

export const ArchetypeImpactDisclosure: React.FC<Props> = ({
  archetype,
  defaultOpen = false,
  testIdPrefix = 'archetype-impact',
}) => {
  // Null archetype — site/company both unset. Render the explainer copy
  // inline (no disclosure needed) so the user understands what "neutral
  // framing" means and where to set a company default.
  if (!archetype) {
    return (
      <div
        className="border border-dashed border-border bg-state-hover/40 p-3 text-[10px] text-text-secondary leading-relaxed"
        data-testid={`${testIdPrefix}-neutral`}
      >
        <span className="font-bold uppercase tracking-widest text-text-muted">No archetype effective</span>
        <span className="block mt-1">
          Reports use neutral developer/owner framing. Set a company default in Settings → Company → Archetype, or pick a site-level archetype above, to specialise the narrative.
        </span>
      </div>
    );
  }

  const meta = COMPANY_ARCHETYPES[archetype];
  if (!meta) {
    // Unknown archetype id — bias safe: render nothing rather than
    // crashing or showing a misleading placeholder.
    return null;
  }

  return (
    <details
      className="border border-border bg-state-hover/40 text-[10px] text-text-secondary"
      open={defaultOpen}
      data-testid={`${testIdPrefix}-disclosure`}
      data-archetype={archetype}
    >
      <summary
        className="cursor-pointer select-none px-3 py-2 font-bold uppercase tracking-widest text-text-secondary hover:text-text-primary"
        data-testid={`${testIdPrefix}-summary`}
      >
        How this affects the report
      </summary>
      <div className="px-3 pb-3 pt-1 space-y-3">
        <div>
          <div className="type-body font-bold uppercase tracking-widest text-text-muted">Reader lens</div>
          <p className="mt-0.5 leading-relaxed" data-testid={`${testIdPrefix}-reader-lens`}>
            {meta.readerLens}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span className="type-body font-bold uppercase tracking-widest text-text-muted">Default risk tolerance</span>
          <span
            className="type-body font-mono uppercase tracking-widest px-1.5 py-0.5 border border-border text-text-secondary"
            data-testid={`${testIdPrefix}-risk-tolerance`}
            data-value={meta.defaultRiskTolerance}
          >
            {RISK_LABEL[meta.defaultRiskTolerance]}
          </span>
        </div>

        {meta.typicalDecisions.length > 0 && (
          <div>
            <div className="type-body font-bold uppercase tracking-widest text-text-muted">Typical decisions</div>
            <ul className="mt-1 list-disc pl-4 space-y-0.5">
              {meta.typicalDecisions.map((d, i) => (
                <li key={i} data-testid={`${testIdPrefix}-typical-decision-${i}`}>{d}</li>
              ))}
            </ul>
          </div>
        )}

        {meta.narrativeShifts.length > 0 && (
          <div>
            <div className="type-body font-bold uppercase tracking-widest text-text-muted">Narrative shifts vs. neutral framing</div>
            <ul className="mt-1 list-disc pl-4 space-y-0.5">
              {meta.narrativeShifts.map((s, i) => (
                <li key={i} data-testid={`${testIdPrefix}-narrative-shift-${i}`}>{s}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </details>
  );
};
